import React from 'react';
import clsx from 'clsx';
import {
	ClickAwayListener,
	type MenuProps as MuiMenuProps,
	Popover,
	type PopoverActions,
} from '@mui/material';
import {type ActionMenuListProps} from '@netx/core-components/ActionMenuList';
import {useDocumentKeyDown} from '@netx/core-components/hooks/useDocumentKeyDown';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {
	ACTION_MENU_CLASS_NAME,
	actionMenuClasses,
	type ActionMenuClasses,
	getActionMenuUtilityClass,
} from './actionMenuClasses';
import {ActionMenuHeader, type ActionMenuHeaderProps} from './ActionMenuHeader';
import {ActionMenuListWrapper} from './ActionMenuListWrapper';
import {ActionMenuProvider} from './contexts';

type ActionMenuOwnerState = WithClasses<ActionMenuClasses>;

const useUtilityClasses = (ownerState: ActionMenuOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		wrapper: ['wrapper'],
	};
	return composeClasses(slots, getActionMenuUtilityClass, classes);
};

const ActionMenuRoot = styled(Popover, {
	name: ACTION_MENU_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	'& .MenuHeader-root': {
		borderRadius: '8px 8px 0 0',
	},
	// prevent the main popover element from obstructing other menus
	'& .MuiPopover-root[role="presentation"]': {
		pointerEvents: 'none',
		// but we still want the menu itself to be interactable
		'& .MuiPopover-paper': {
			pointerEvents: 'auto',
		},
	},
	'& > .MuiPopover-paper': {
		display: 'flex',
		overflowY: 'initial',
		overflowX: 'initial',
	},
}));

const ActionMenuWrapper = styled('div', {
	name: ACTION_MENU_CLASS_NAME,
	slot: 'Wrapper',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	flexDirection: 'column',
	overflow: 'hidden',
}));

export interface ActionMenuProps
	extends Omit<MuiMenuProps, 'classes' | 'sx' | 'title'>,
		Pick<
			ActionMenuHeaderProps,
			'title' | 'secondaryTitle' | 'icon' | 'severity'
		>,
		Pick<ActionMenuListProps, 'actions' | 'onAction'>,
		WithClasses<ActionMenuClasses> {
	ActionMenuHeader?: React.ReactElement | null;
}

export const ActionMenuPrimitive = ({
	classes: _classes,
	className,
	actions,
	icon,
	title,
	secondaryTitle,
	severity,
	slotProps,
	onAction,
	onClick,
	onClose,
	onMouseDown,
	ActionMenuHeader: _ActionMenuHeader,
	MenuListProps,
	TransitionProps,
	...props
}: ActionMenuProps) => {
	const actionRef = React.useRef<PopoverActions>(null);
	const paperRef = React.useRef<HTMLDivElement>(null);

	const ownerState: ActionMenuOwnerState = {classes: _classes, className};
	const classes = useUtilityClasses(ownerState);

	useDocumentKeyDown((event) => {
		if (event.key === 'Escape') {
			onClose?.({}, 'escapeKeyDown');
			return false;
		}
	});

	return (
		<ActionMenuProvider mainMenuOpen={props.open}>
			{({activeDepth, setActiveDepth, handleDepthLeft}) => (
				<ActionMenuRoot
					{...props}
					className={clsx(classes.root, className)}
					slotProps={{
						...slotProps,
						paper: {
							...slotProps?.paper,
							// this is the ref we will use to position the child menu(s)
							ref: paperRef,
						},
					}}
					onClose={splitEventHandler(onClose, () => setActiveDepth(-1))}
					// we want to prevent the menu from closing when the user clicks within the menu
					onClick={splitEventHandler(onClick, (event) => {
						event.stopPropagation();
					})}
					onMouseDown={splitEventHandler(onMouseDown, (event) => {
						event.stopPropagation();
					})}
					TransitionProps={{
						...TransitionProps,
						// we need to update the position of the menu when it enters the DOM
						onEntered: splitEventHandler(TransitionProps?.onEntered, () => {
							actionRef.current?.updatePosition();
						}),
					}}
				>
					<ClickAwayListener onClickAway={() => onClose?.({}, 'backdropClick')}>
						<ActionMenuWrapper className={classes.wrapper}>
							{_ActionMenuHeader
								? _ActionMenuHeader
								: title != null && (
										<ActionMenuHeader
											icon={icon}
											title={title}
											secondaryTitle={secondaryTitle}
											severity={severity}
										/>
								  )}

							<ActionMenuListWrapper
								action={actionRef}
								actions={actions}
								parentMenuRef={paperRef}
								parentMenuOpen={props.open}
								onAction={onAction}
								// {...MenuListProps}
								// we want to prevent the menu from closing when the user presses the left arrow key
								// we just want to go back a depth level
								onKeyDown={splitEventHandler(
									MenuListProps?.onKeyDown,
									(event) => {
										switch (event.key) {
											case 'ArrowLeft':
												event.stopPropagation();
												handleDepthLeft(activeDepth);
												break;
										}
									},
								)}
								// when the user enters the master parent menu, we want to reset the active depth
								onMouseEnter={splitEventHandler(
									MenuListProps?.onMouseEnter,
									() => setActiveDepth(0),
								)}
								// when the user leaves the master parent menu, we want to reset the active depth but delayed
								// for ux purposes
								onMouseLeave={splitEventHandler(
									MenuListProps?.onMouseLeave,
									() => handleDepthLeft(0, 500),
								)}
							/>
						</ActionMenuWrapper>
					</ClickAwayListener>
				</ActionMenuRoot>
			)}
		</ActionMenuProvider>
	);
};

/**
 * The `ActionMenu` component is a wrapper around the MUI `Popover` component that provides a
 * consistent API for creating menus with a header and a list of actions that can go down as many levels as needed.
 */
export const ActionMenu = ActionMenuPrimitive;
